import { DefaultButton, DirectionalHint, IButtonStyles, ITextField, Stack, TextField, TooltipHost } from "@fluentui/react";
import styles from "./Messages.module.css";
import { CopyButton } from "../common/Button";
import { ChatMessage } from "../../api";
import { ErrorCircleRegular } from "@fluentui/react-icons";
import { useState } from "react";
import { QuestionInput } from "../QuestionInput";
import FileDisplay from "./FileDisplay";

interface UserMessageProps {
    answer: ChatMessage
    isEditable: boolean
    isCopyable: boolean
    conversation_id?: string
    onEditSend: (question: string, conversation_id: string | undefined, message_id: string, tools: string[], toolArgs: Map<string, Map<string, any>>) => Promise<void>;
}
export const UserMessage = (props: UserMessageProps) => {
    const { answer, isEditable, isCopyable, onEditSend, conversation_id } = props
    const contentStr = answer.contentType ? (answer.fileName ?? "") : answer.content
    const [isEditing, setIsEditing] = useState(false)
    const [edited, setEdited] = useState(contentStr)
    const [tools, setTools] = useState<string[]>([])
    const [isSending, setIsSending] = useState(false)

    const [toolArgs, setToolArgsMap] = useState<Map<string, Map<string, any>>>(new Map())
    const setToolArgs = (toolName: string, argName: string, value: any) => {
        setToolArgsMap(prev => {
            const next = new Map(prev)
            const nextToolArgs = new Map(next.get(toolName) ?? new Map<string, any>())
            nextToolArgs.set(argName, value)
            next.set(toolName, nextToolArgs)
            return next
        })
    }

    const handleEditToggle = () => {
        setEdited(contentStr);
        setTools([])
        setIsEditing(!isEditing);
    };

    const buttonStyles: IButtonStyles = {
        root: {
          minWidth: '30px',
          border: 0,
          borderRadius: 4,
          padding: '0 4px',
          backgroundColor: "transparent",
        },
    };

    return (
        <div className={styles.chatMessageUser} tabIndex={0} data-testid="user-message">
            {
                <Stack className={isEditing ? styles.chatMessageUserMessageEditing : styles.chatMessageUserMessage}>
                    {
                        isEditing ? 
                        <QuestionInput 
                            onSend={async (question, id, tools) => {
                                setIsSending(true)
                                try{
                                    await onEditSend(question, id, answer.id, tools, toolArgs)
                                } finally {
                                    setIsSending(false)
                                }
                            }}
                            disabled={isSending}
                            sendNotAllowed={isSending}
                            clearOnSend={false}
                            conversationId={conversation_id}
                            question={edited}
                            setQuestion={setEdited} 
                            files={[]}
                            isFileSelected={false}
                            messages={[]}
                            tools={tools}
                            setTools={setTools}
                            toolArgs={toolArgs}
                            setToolArgs={setToolArgs}
                            testIdPrefix="edit-"
                        />
                        :
                        contentStr
                    }
                </Stack>
            }
            <Stack style={{marginTop: '4px'}} horizontal>
                {
                    isEditable && !isSending &&
                    <TooltipHost
                      content={isEditing ? "変更を破棄して戻る" : "編集して再送信"}
                      directionalHint={DirectionalHint.bottomCenter}
                    >
                      <DefaultButton
                        iconProps={{ iconName: isEditing ? 'Undo' : 'Edit' }}
                        styles={buttonStyles}
                        onClick={handleEditToggle}
                        data-testid="edit-message-button"
                        data-checked={isEditing}
                      />
                    </TooltipHost>
                }
                {
                    isCopyable && !isSending && <CopyButton copyText={answer.content} testId="copy-user-message-button"/>
                }
            </Stack>
        </div>
    )
}

interface UserFileMessageProps {
    answer: ChatMessage
}

export const UserFileMessage = (props: UserFileMessageProps) => {
    const { answer } = props
    return (
        <div className={styles.chatMessageUser} tabIndex={0} data-testid="user-file-message">
            <Stack className={styles.chatMessageUserFileMessage}>
                <FileDisplay
                    fileName={answer.fileName ? answer.fileName : ""}
                    blobFileName={answer.content}
                    fileAreaMaxHeight="300px"
                />
            </Stack>
        </div>
    )
}

interface ErrorMessageProps {
    answer: ChatMessage
}
export const ErrorMessage = (props: ErrorMessageProps) => {
    const { answer } = props
    return (
        <div className={styles.chatMessageError}>
            <Stack horizontal className={styles.chatMessageErrorContent}>
                <ErrorCircleRegular style={{color: "rgba(182, 52, 67, 1)"}} />
                <span>Error</span>
            </Stack>
            <span className={styles.chatMessageErrorContent}>{answer.content}</span>
        </div> 
    )
}